import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ToolContext } from "../context/ToolContext";
import { FormContext } from "../context/FormContext";
import { ClientSettingsFilterResults } from "../model/reference/settingsTypes";
import { SimpleFilterResultsInfo } from "../components/SimpleFilterResultsInfo";
import { getLocalStorageValue, setLocalStorageValue } from "../utils/useLocalStorage";
import { applyFilterPreset } from "../utils/applyFilterPreset";
import { getWarningInfos as computeWarningInfos } from "../utils/getWarningInfos";
import { resultsAreShown as compareResults } from "../utils/resultsAreShown";
import { getCustomResultKeys } from "./filterResultFunctions";
import * as filterResultPresetConst from "../constants/filterResultPresetConst";
import {
  CUSTOM_SETTINGS_KEY,
  Fault,
  FilterResultPreset,
  SeasonableInfo,
} from "../components/FilterResultHeader/FilterResultHeader";

export type TooltipState = {
  save: boolean;
  clear: boolean;
  seasons: boolean;
  load: boolean;
  display: boolean;
};

export function useFilterResultHeader(faults: Fault[], getOverallSeason: () => string) {
  const { toolState } = useContext(ToolContext);
  const { formState, dispatch } = useContext(FormContext);

  const [customSettings, setCustomSettings] = useState<SimpleFilterResultsInfo[]>([]);
  const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState<TooltipState>({
    save: false,
    clear: false,
    seasons: false,
    load: false,
    display: false,
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState<string>(filterResultPresetConst.Defaults);

  const filterResults: ClientSettingsFilterResults[] = useMemo(
    () =>
      formState?.clientSettings?.filterResults ? [...formState.clientSettings.filterResults] : [],
    [formState?.clientSettings?.filterResults],
  );

  const filterResultPresets: FilterResultPreset[] = useMemo(
    () =>
      formState?.clientSettings?.filterResultPresets
        ? [...formState.clientSettings.filterResultPresets]
        : [],
    [formState?.clientSettings?.filterResultPresets],
  );

  const presetNames = useMemo(() => filterResultPresets.map((p) => p.name), [filterResultPresets]);

  const resolvedPresets = useMemo(() => {
    return filterResultPresets.reduce(
      (acc, preset) => {
        acc[preset.name] = applyFilterPreset(
          formState?.originalClientSettings?.filterResults ?? [],
          filterResultPresets,
          preset.name,
        );
        return acc;
      },
      {} as Record<string, ClientSettingsFilterResults[]>,
    );
  }, [filterResultPresets, formState?.originalClientSettings?.filterResults]);

  const clientDefaultInfos: SimpleFilterResultsInfo[] = useMemo(
    () =>
      formState?.originalClientSettings?.filterResults?.flatMap((r: ClientSettingsFilterResults) =>
        r.info.map((i) => SimpleFilterResultsInfo.getNew(i)),
      ) ?? [],
    [formState?.originalClientSettings?.filterResults],
  );

  useEffect(() => {
    setCustomSettings(getLocalStorageValue(CUSTOM_SETTINGS_KEY, []));
  }, []);

  const resultsAreShown = (infos: SimpleFilterResultsInfo[] | null | undefined) =>
    compareResults(filterResults, infos ?? []);

  useEffect(() => {
    if (!resultsAreShown(clientDefaultInfos)) dispatchInfos(clientDefaultInfos);
    setSelectedResult(filterResultPresetConst.Defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    var matchingPresets = [];
    var matchingPreset = filterResultPresets.find((preset) => {
      const clientSettingsFilterResults = resolvedPresets[preset.name];
      if (!clientSettingsFilterResults) return false;
      const simpleFilterResultsInfos = clientSettingsFilterResults
        .flatMap((x) => x.info)
        .map((i) => SimpleFilterResultsInfo.getNew(i));
      return resultsAreShown(simpleFilterResultsInfos);
    })?.name;

    if (matchingPreset) matchingPresets.push(matchingPreset);
    if (resultsAreShown([])) matchingPresets.push(filterResultPresetConst.Clear);
    if (resultsAreShown(clientDefaultInfos)) matchingPresets.push(filterResultPresetConst.Defaults);
    if (resultsAreShown(customSettings)) matchingPresets.push(filterResultPresetConst.Custom);

    if (!!matchingPresets.length) {
      if (!matchingPresets.includes(selectedResult)) {
        setSelectedResult(matchingPresets[0]);
      }
    } else setSelectedResult(filterResultPresetConst.CustomUnsaved);
  }, [filterResults, customSettings]);

  const toggleTooltip = (key: keyof TooltipState) =>
    setTooltipOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const dropdownToggle = () => setDropdownOpen((p) => !p);

  const isAtLeastOneItemTicked = useMemo(() => {
    return filterResults.some((fr) => fr.info.some((info) => info.isSet));
  }, [filterResults]);

  const hasSeasons = useMemo(
    () =>
      filterResults.some((fr) =>
        fr.info.some((info) => info.isSet && !!(info as SeasonableInfo).season),
      ),
    [filterResults],
  );

  const dispatchInfos = useCallback(
    (infos: SimpleFilterResultsInfo[]): void => {
      formState.clientSettings.filterResults = formState.clientSettings.filterResults.map(
        (fr: ClientSettingsFilterResults) => {
          for (const formInfoRaw of fr.info) {
            const formInfo = formInfoRaw as SeasonableInfo;

            let newInfo: SimpleFilterResultsInfo | undefined;
            if (formInfo.options?.length) {
              newInfo = infos.find((i) => i.name === formInfo.name);
              formInfo.value = newInfo?.value ?? "";
            } else {
              newInfo = infos.find((i) => i.name === formInfo.name && i.value === formInfo.value);
            }

            formInfo.isSet = newInfo?.isSet ?? false;

            const sNew = (newInfo as (SimpleFilterResultsInfo & { season?: string }) | undefined)
              ?.season;
            if (sNew !== undefined) formInfo.season = sNew;
          }

          return fr;
        },
      );

      dispatch({ form: "clientSettings", obj: formState.clientSettings, type: "REPLACE_STATE" });
    },
    [dispatch, formState],
  );

  const getWarningInfos = useCallback(
    () => computeWarningInfos(clientDefaultInfos, faults),
    [clientDefaultInfos, faults],
  );

  const showAllWarnings = () => dispatchInfos(getWarningInfos());

  const updateOverallSeason = (season: string): void => {
    const infos: SimpleFilterResultsInfo[] = [];
    filterResults.forEach((fr) => {
      fr.info
        .filter((info) => info.isSet)
        .forEach((info) => {
          const newInfo = SimpleFilterResultsInfo.getNew(info);
          if ((info as SeasonableInfo).season) {
            newInfo.season = season;
          }
          infos.push(newInfo);
        });
    });
    dispatchInfos(infos);
  };

  const saveCustom = (okClicked: boolean) => {
    setShowOverwriteWarning(false);
    if (!okClicked) return;

    const infos: SimpleFilterResultsInfo[] = filterResults
      .flatMap((r: ClientSettingsFilterResults) =>
        r.info.map((i) => SimpleFilterResultsInfo.getNew(i)),
      )
      .filter((i) => i.isSet);

    if (infos?.length) {
      setLocalStorageValue(CUSTOM_SETTINGS_KEY, infos);
      setCustomSettings(infos);
    }

    // defer until after state/dispatch cycles
    Promise.resolve().then(() => {
      getCustomResultKeys();
      setSelectedResult(filterResultPresetConst.Custom);
    });
  };

  const handleSelectPreset = (presetName: string) => {
    if (presetName === filterResultPresetConst.Clear) {
      if (!resultsAreShown([])) dispatchInfos([]);
      setSelectedResult(filterResultPresetConst.Clear);
      return;
    }
    if (presetName === filterResultPresetConst.Defaults) {
      if (!resultsAreShown(clientDefaultInfos)) dispatchInfos(clientDefaultInfos);
      setSelectedResult(filterResultPresetConst.Defaults);
      return;
    }
    if (presetName === filterResultPresetConst.Custom) {
      const infos = (
        customSettings?.length ? customSettings : getLocalStorageValue(CUSTOM_SETTINGS_KEY, [])
      ) as SimpleFilterResultsInfo[] | null | undefined;
      dispatchInfos((infos ?? []) as SimpleFilterResultsInfo[]);
      setSelectedResult(filterResultPresetConst.Custom);
      return;
    }

    const updatedFilterResults = resolvedPresets[presetName];
    if (!updatedFilterResults) return;
    const next = { ...formState };
    next.clientSettings.filterResults = JSON.parse(JSON.stringify(updatedFilterResults));
    dispatch({ form: "clientSettings", obj: next.clientSettings, type: "REPLACE_STATE" });
    setSelectedResult(presetName);
  };

  const hasWarnings = useMemo(
    () => toolState.showResults && faults.some((p) => p.constraintCount > 0),
    [faults, toolState.showResults],
  );

  const canShowWarnings = useMemo(
    () =>
      toolState.showResults &&
      faults.some((p) => p.constraintCount > 0) &&
      !resultsAreShown(getWarningInfos()),
    [faults, getWarningInfos, filterResults, toolState.showResults],
  );

  const canSave = useMemo(
    () => isAtLeastOneItemTicked && !resultsAreShown(customSettings),
    [isAtLeastOneItemTicked, resultsAreShown, customSettings],
  );

  return {
    tooltipOpen,
    dropdownOpen,
    selectedResult,
    showOverwriteWarning,
    hasSeasons,
    presetNames,
    hasWarnings,
    canShowWarnings,
    canSave,

    setShowOverwriteWarning,
    toggleTooltip,
    dropdownToggle,
    handleSelectPreset,
    saveCustom,
    showAllWarnings,
    updateOverallSeason,

    getOverallSeason,
  };
}
